import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import SectionTitle from "./SectionTitle";
import { useSiteAboutData } from "../hooks/useSiteData";
import { type } from "../lib/typography";

function AboutImage({ src, className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-xl shadow-lg ${className}`}>
      <img
        src={src}
        alt="SSA Techno project showcase"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105"
        loading="lazy"
      />
    </div>
  );
}

function AboutImageGrid({ images }) {
  if (!images.length) return null;

  if (images.length === 1) {
    return <AboutImage src={images[0]} className="aspect-[4/5] w-full" />;
  }

  if (images.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {images.map((src) => (
          <AboutImage key={src} src={src} className="aspect-[3/4]" />
        ))}
      </div>
    );
  }

  if (images.length === 3) {
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-3 sm:gap-4 h-[420px] sm:h-[480px]">
        <AboutImage src={images[0]} className="row-span-2 h-full" />
        <AboutImage src={images[1]} className="h-full" />
        <AboutImage src={images[2]} className="h-full" />
      </div>
    );
  }

  // 4 images — tall featured left, one top-right, two bottom-right
  return (
    <div className="grid grid-cols-2 grid-rows-2 gap-3 sm:gap-4 h-[400px] sm:h-[480px]">
      <AboutImage src={images[0]} className="row-span-2 h-full" />
      <AboutImage src={images[1]} className="h-full" />
      <div className="grid grid-cols-2 gap-3 sm:gap-4 h-full min-h-0">
        <AboutImage src={images[2]} className="h-full" />
        <AboutImage src={images[3]} className="h-full" />
      </div>
    </div>
  );
}

export default function AboutPreview() {
  const { data: about } = useSiteAboutData();

  if (!about) return null;

  const homepageImages = (about.homepageImageUrls || []).slice(0, 4);
  const hasImages = homepageImages.length > 0;
  const bulletPoints = about.bulletPoints || [];

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      <div
        className={`max-w-7xl mx-auto px-6 grid gap-14 items-center ${
          hasImages ? "lg:grid-cols-2" : "max-w-3xl"
        }`}
      >
        {hasImages && (
          <div className="relative">
            <AboutImageGrid images={homepageImages} />
            {/* <div className="absolute bottom-4 left-4 bg-[#1e6fcf] text-white px-6 py-5 rounded-lg shadow-2xl z-10"> */}
              {/* <div className="text-4xl font-black leading-none">{about.experience}</div> */}
              {/* <div className="text-xs uppercase tracking-wider font-semibold mt-1">Years of Excellence</div> */}
            {/* </div> */}
          </div>
        )}

        <div className={hasImages ? "" : "mx-auto w-full"}>
          <SectionTitle
            eyebrow="About SSA Techno"
            title={about.mainTitle}
            subtitle={about.subTitle}
          />
          {bulletPoints.length > 0 && (
            <ul className="mt-8 space-y-3">
              {bulletPoints.map((point) => (
                <li key={point.id} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#1e6fcf] flex-shrink-0 mt-0.5" />
                  <span className={type.body}>{point.text}</span>
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/about"
            className="mt-10 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-[#0a1a2f] shadow-sm transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white"
          >
            Discover Our Story
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
